import { NewJoinersAndAllocationsModalContext } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Modals/NewJoinersAndAllocationsModal/NewJoinersAndAllocationsModal';
import {
	ContractType,
	DataContractTct,
	DocumentWorkflowTemplateVisibleIn,
	SignatureType,
	InvestmentWorkflowType,
} from '@dealstack/shared/lib/generated';
import { generateUUID } from '@dealstack/frontend/shared/other/react/generateUUID';
import { useContext, useEffect, useMemo, useState } from 'react';
import { Path, useFieldArray } from 'react-hook-form';
import { NewLeaverContractsFormValues } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Modals/NewLeaversModal/useNewLeaversModalController';
import {
	fileNameToDocumentName,
	formatFileNameAppend,
} from '@dealstack/shared/lib/common/file/file';
import { FileExtension } from '@dealstack/shared/lib/common/dataExchange/common';
import { useDownloadFileUsingFileStorageUrl } from '@dealstack/frontend/shared/modules/document/file/hooks/useDownloadFileUsingFileStorageUrl';

export const useNewJoinersAndAllocationsContractsController = () => {
	const { contractForm, mipData, setDisableContinue, isNewJoinersModal } = useContext(
		NewJoinersAndAllocationsModalContext
	);
	const { downloadFile } = useDownloadFileUsingFileStorageUrl();

	const [showCounterpartiesModal, setShowCounterpartiesModal] = useState(false);
	const [showInvestorsModal, setShowInvestorsModal] = useState(false);
	const [currentRowIndex, setCurrentRowIndex] = useState(0);
	const [instructionInputValueBackup, setInstructionInputValueBackup] = useState('');

	const {
		fields: contractFields,
		append,
		remove,
		update,
	} = useFieldArray({
		name: 'contracts',
		control: contractForm.control,
		// we use keyName to prevent override backend contract id by rhf in fields
		keyName: 'key' as 'id',
	});

	const contractFormWatch = contractForm.watch();

	const selectedFieldsToWatch = useMemo(() => {
		if (!contractFormWatch.contracts) {
			return [];
		}

		const fields: Path<NewLeaverContractsFormValues>[] = [];

		for (let i = 0; i < contractFormWatch.contracts.length; i++) {
			fields.push(`contracts.${i}.type`, `contracts.${i}.signatureType`);
		}

		return fields;
	}, [contractFormWatch]);

	const contractSelectedFieldsFormWatch = contractForm.watch(selectedFieldsToWatch);

	useEffect(() => {
		if (!contractFormWatch.contracts) {
			return;
		}

		for (const [index, contract] of contractFormWatch.contracts.entries()) {
			if (
				(contract.type === ContractType.FORM ||
					contract.signatureType === SignatureType.REVIEW) &&
				contract.hasWitnesses
			) {
				contractForm.setValue(`contracts.${index}.hasWitnesses`, false);
			}
		}
	}, [...contractSelectedFieldsFormWatch, contractFormWatch.contracts]);

	const onDownloadDocumentTemplate = (fileId: string, fileName: string) => {
		void downloadFile({ fileId, dealId: mipData?.dealId ?? '', fileName });
	};

	const onUploadFileLocally = (acceptedFiles: File[]) => {
		const defaultApplicability = isNewJoinersModal
			? [InvestmentWorkflowType.JOINER]
			: [InvestmentWorkflowType.ALLOCATION];

		acceptedFiles.forEach((file) => {
			append({
				file: {
					name: '',
					id: '',
					extension: '',
					s3key: '',
				},
				fileInstance: file,
				name: fileNameToDocumentName(file.name),
				instruction: '',
				isExpired: false,
				isRejected: false,
				archived: false,
				requiredWitnesses: 0,
				counterparties: [],
				id: generateUUID(),
				//NOTE: Darktrace quick change should be electronic
				signatureType: SignatureType.ELECTRONIC,
				//NOTE: Darktrace quick change should be deal
				type: ContractType.SIDE_LETTER,
				local: true,
				hasWitnesses: false,
				applicability: defaultApplicability,
				visibleIn: [DocumentWorkflowTemplateVisibleIn.INVESTMENT_WORKFLOWS_BUYING],
			});
		});
	};

	const visibleContracts = useMemo(
		() =>
			mipData?.contractFiles
				.filter((contract) =>
					contract.visibleIn?.includes(
						DocumentWorkflowTemplateVisibleIn.INVESTMENT_WORKFLOWS_BUYING
					)
				)
				.filter((contract) => contract.type !== ContractType.SIDE_LETTER),
		[mipData?.contractFiles]
	);

	const onRemoveRowClicked = (rowID: string) => {
		const formContracts = contractForm.getValues('contracts');
		if (formContracts) {
			const indexToRemove = contractFields.findIndex((item) => item.id === rowID);
			if (indexToRemove < 0) {
				return;
			}
			remove(indexToRemove);
		}
	};

	const openInvestorModal = (id: string) => {
		const currentContractIndex = contractFields.findIndex((item) => item.id === id);
		setCurrentRowIndex(currentContractIndex);
		setShowInvestorsModal(true);
		const contract = getCurrentContract(currentContractIndex);
		setInstructionInputValueBackup(contract.instruction);
	};
	const openCounterpartiesModal = (id: string) => {
		setCurrentRowIndex(contractFields.findIndex((item) => item.id === id));
		setShowCounterpartiesModal(true);
		setInstructionInputValueBackup('');
	};

	const onInstructionLocalSave = () => {
		update(currentRowIndex, getCurrentContract());
		setShowInvestorsModal(false);
	};

	const onCounterpartiesLocalSave = () => {
		update(currentRowIndex, getCurrentContract());
		setShowCounterpartiesModal(false);
	};
	const onInstructionLocalCancel = () => {
		const contract = getCurrentContract();
		contractForm.setValue(`contracts.${currentRowIndex}`, {
			...contract,
			instruction: instructionInputValueBackup,
		});
		setInstructionInputValueBackup('');
		setShowInvestorsModal(false);
	};
	const getCurrentContract = (currentContractIndex?: number) => {
		return contractForm.getValues(`contracts.${currentContractIndex ?? currentRowIndex}`);
	};

	useEffect(() => {
		const formContracts = contractForm.getValues('contracts');
		const shouldDisableContinue =
			formContracts?.some((contract) => contract.name.trim() === '') ?? false;
		setDisableContinue(shouldDisableContinue);
	}, [contractForm.getValues()]);

	const onDownloadContractInstruction = async (contract: DataContractTct) => {
		const { textToPDF } = await import('@dealstack/frontend/shared/other/react/textToPDF');

		await textToPDF({
			pdfHeader: `Instruction for contract "${contract.name}"`,
			pdfText: contract.instruction,
			downloadFileName: formatFileNameAppend(
				contract.file.name,
				'-instruction',
				FileExtension.pdf
			),
		});
	};

	return {
		contractForm,
		showInvestorsModal,
		setShowInvestorsModal,
		currentRowIndex,
		onInstructionLocalSave,
		onInstructionLocalCancel,
		showCounterpartiesModal,
		setShowCounterpartiesModal,
		onCounterpartiesLocalSave,
		visibleContracts,
		contractFields,
		openCounterpartiesModal,
		openInvestorModal,
		onRemoveRowClicked,
		onUploadFileLocally,
		onDownloadDocumentTemplate,
		onDownloadContractInstruction,
	};
};
